import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Snackbar
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from "@material-ui/icons";
import axios from "axios";
import envConfig from "../../../bin/env.config";
import { useSelector } from "react-redux";

export default function ImportCSVHistoryList({ project_id }) {
  const token = useSelector((state) => state.session.token);
  
  // State management
  const [importLogs, setImportLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  // Fetch import history data
  const fetchImportHistory = async () => {
    if (!project_id || !token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/project/import-csv/log-get-list`;
      const response = await axios.post(url, {
        project_id
      }, {
        headers: {
          Authorization: JSON.stringify(token),
        },
      });
      
      // Ensure we have an array of import logs
      const data = response.data;
      
      if (Array.isArray(data)) {
        setImportLogs(data);
      } else if (data && Array.isArray(data.data)) {
        setImportLogs(data.data);
      } else if (data && Array.isArray(data.importLogs)) {
        setImportLogs(data.importLogs);
      } else {
        setImportLogs([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch import history');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when project_id changes
  useEffect(() => {
    fetchImportHistory();
  }, [project_id, token]);

  // Toggle expanded state for individual items
  const toggleExpanded = (logId) => {
    setExpandedItems(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (err) {
      return dateString;
    }
  };

  // Get status chip color
  const getStatusColor = (successCount, errorCount) => {
    if (errorCount === 0) return 'primary';
    if (successCount === 0) return 'secondary';
    return 'default';
  };

  // Get status text
  const getStatusText = (successCount, errorCount) => {
    if (errorCount === 0) return 'Completed';
    if (successCount === 0) return 'Failed';
    return 'Partial';
  };

  // Render import log item
  const renderImportLogItem = (log) => {
    if (!log || typeof log !== 'object') {
      return null;
    }
    
    const isExpanded = expandedItems[log.id];
    const statusColor = getStatusColor(log.total_success, log.total_error);
    const statusText = getStatusText(log.total_success, log.total_error);
    
    return (
      <Card key={log.id} style={{ marginBottom: 8 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" component="div">
                {log.file_name || 'Unknown File'}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={statusText}
                  color={statusColor}
                  size="small"
                  style={{
                    backgroundColor: statusText === 'Completed' ? '#4caf50' : 
                                   statusText === 'Failed' ? '#f44336' : '#ff9800',
                    color: 'white'
                  }}
                />
                <IconButton
                  onClick={() => toggleExpanded(log.id)}
                  size="small"
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
          }
          subheader={
            <Box>
              <Typography variant="body2" color="textSecondary">
                Imported on: {formatDate(log.created_at)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Code: {log.code || 'N/A'}
              </Typography>
            </Box>
          }
        />
        
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon style={{ marginRight: 8, fontSize: 16, color: '#4caf50' }} />
                        Total Rows
                      </Box>
                    </TableCell>
                    <TableCell align="right">{log.total_row || 0}</TableCell>
                    <TableCell align="right">100%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <Box display="flex" alignItems="center">
                        <CheckCircleIcon style={{ marginRight: 8, fontSize: 16, color: '#4caf50' }} />
                        Successful
                      </Box>
                    </TableCell>
                    <TableCell align="right">{log.total_success || 0}</TableCell>
                    <TableCell align="right">
                      {log.total_row > 0 ? 
                        `${Math.round((log.total_success / log.total_row) * 100)}%` : 
                        '0%'
                      }
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <Box display="flex" alignItems="center">
                        <ErrorIcon style={{ marginRight: 8, fontSize: 16, color: '#f44336' }} />
                        Errors
                      </Box>
                    </TableCell>
                    <TableCell align="right">{log.total_error || 0}</TableCell>
                    <TableCell align="right">
                      {log.total_row > 0 ? 
                        `${Math.round((log.total_error / log.total_row) * 100)}%` : 
                        '0%'
                      }
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  // Render loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 8 }}>
          Loading import history...
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Paper style={{ padding: 16, backgroundColor: '#ffebee', border: '1px solid #f44336' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <ErrorIcon color="error" style={{ marginRight: 8 }} />
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
          <Button color="primary" size="small" onClick={fetchImportHistory}>
            <RefreshIcon style={{ marginRight: 4 }} />
            Retry
          </Button>
        </Box>
      </Paper>
    );
  }

  // Render empty state
  if (!importLogs || importLogs.length === 0) {
    return (
      <Box textAlign="center" p={2}>
        <InfoIcon color="disabled" style={{ fontSize: 48, marginBottom: 8 }} />
        <Typography variant="body2" color="textSecondary">
          No import history found for this project
        </Typography>
        <Button 
          size="small" 
          onClick={fetchImportHistory}
          style={{ marginTop: 8 }}
        >
          <RefreshIcon style={{ marginRight: 4 }} />
          Refresh
        </Button>
      </Box>
    );
  }

  // Render import history list
  return (
    <Box>
      <Divider style={{ marginTop: 16,marginBottom: 16 }} />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Import History ({importLogs.length})
        </Typography>
        <Button 
          size="small" 
          onClick={fetchImportHistory}
          startIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </Box>
      
      <Divider style={{ marginBottom: 16 }} />
      
      <Box maxHeight={400} overflow="auto">
        {Array.isArray(importLogs) ? importLogs.map(renderImportLogItem) : null}
      </Box>
    </Box>
  );
}
